import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { ExtendedNavId } from '@employer/app/components/layout/main-layout.interface'
import { EmployerNavigationService, ROUTES } from '@employer/app/services/navigation.service'
import { BehaviorSubject, Subject } from 'rxjs'
import { waitForTourAnchor } from '../utils/tour-anchor.util'
import { ArrowSide } from '../utils/tour-position.util'
import { isLeftNavVisible, needsAccountMenu, resolveTourTarget } from '../utils/tour-target.util'
import { TourFocusService } from './tour-focus.service'
import { TourEngineService } from './tour-engine.service'

/** A single stop in the floating-card walkthrough — one per nav AREA, plus a final closing step. */
export interface IGuideStep {
  id: string
  /** Router url for a navigating stop. Omitted on the intro and closing steps (which change no screen). */
  route?: string
  /** The opening step: an explanation of what the tour will do. It shows no indicator (no ring, arrow or dim). */
  intro?: boolean
  /** Where the item lives, shown as an eyebrow above the title (e.g. "Left navigation", "Account menu").
   *  Omitted on the intro and closing steps. */
  category?: string
  /** PII-free English-as-key narration. */
  title: string
  body: string
  /** `data-tour-id` of the element the ring + arrow point at. Omitted on the intro (which indicates nothing). */
  anchor?: string
  /** Which side of `anchor` the arrow sits on, pointing back at it. Defaults to the right. */
  arrow?: ArrowSide
  /** True when `anchor` lives in the left-nav rail, so it retargets to the hamburger below `lg`. */
  leftNav?: boolean
  /** `data-tour-id` of the nav group to hold expanded while this step shows; every other group collapses. */
  navFocus?: string
  /** Hold the header account dropdown open while this step shows. */
  accountMenu?: boolean
  /**
   * Where this step points when its own anchor is not rendered below `lg` — a different element in
   * different chrome, so it carries its own arrow side and, where that chrome must be opened first,
   * its own `accountMenu`. The closing step uses it: the header's compass is desktop-only, and its
   * job moves to the account menu's "Take a tour". Resolved by `resolveTourTarget` /
   * `needsAccountMenu`, so the card, the focus and the indicator all agree and re-resolve on resize.
   */
  narrow?: { anchor: string; arrow?: ArrowSide; category?: string; accountMenu?: boolean }
  /** Open the desktop messaging panel while this step shows — the panel this row's own click opens. */
  messagingPanel?: boolean
}

/** The current stop the card renders, with its position in the ordered walk. */
export interface IGuideActiveStep {
  step: IGuideStep
  index: number
  total: number
}

/**
 * The ordered walk: one stop per nav AREA (its real nav destination), then a closing step. Gated areas
 * (e.g. Campaigns behind its flag, or role-hidden areas) redirect on navigation and are skipped cleanly
 * by the URL check in `runStep`, so the walk stays role-correct without any per-step permission logic.
 *
 * Every stop past the intro names the element it is about (`anchor`), which side the arrow points from
 * (`arrow` — left-nav rows from the right, account-menu rows from the left, the header launcher from
 * below) and, for the nav areas, which group to hold expanded (`navFocus`).
 *
 * The Favorites stop sits after the nav areas on purpose: pinning shortcuts only means something once
 * you have seen the areas worth pinning.
 */
export const GUIDE_STEPS: IGuideStep[] = [
  {
    id: 'intro',
    intro: true,
    title: 'Quick tour',
    body: "I'll show you around the main areas of the app so you know where everything lives and what each one is for. It takes about a minute — press Start and off we go.",
  },
  {
    id: 'openings',
    route: '/jobs/manage',
    category: 'Left navigation',
    title: 'Openings',
    body: 'Create, edit and publish your openings here, and manage the ones already live.',
    anchor: 'nav-openings',
    arrow: 'right',
    leftNav: true,
    navFocus: 'nav-openings',
  },
  {
    id: 'engage',
    route: '/jobs/list',
    category: 'Left navigation',
    title: 'Pipelines',
    body: 'Work your candidates here: track them through your hiring pipeline, search applicants, and browse your talent pool.',
    anchor: 'nav-engage',
    arrow: 'right',
    leftNav: true,
    navFocus: 'nav-engage',
  },
  {
    id: 'messages',
    route: '/candidate-pool/candidates',
    category: 'Left navigation',
    title: 'Messages',
    body: "Read and reply to your candidate conversations here — the same threads also appear on each candidate's application.",
    anchor: 'nav-messages',
    arrow: 'right',
    leftNav: true,
    // Messages is a leaf row with no children, so nothing expands in the rail — every other group
    // collapses so it is the one thing lit up there.
    navFocus: 'nav-messages',
    // On desktop the row's own click opens the extended messaging panel, so the step opens it too and
    // the user sees what Messages actually does. Note the side effect this inherits from the layout: an
    // open panel forces the primary rail into icon-only mode, so on this step the ring lands on the
    // mail icon rather than the labelled row.
    messagingPanel: true,
  },
  {
    id: 'campaigns',
    route: '/campaigns',
    category: 'Left navigation',
    title: 'Campaigns',
    body: 'Promote your openings to your connected channels and see how they perform.',
    anchor: 'nav-campaigns',
    arrow: 'right',
    leftNav: true,
    navFocus: 'nav-campaigns',
  },
  {
    id: 'reporting',
    route: '/reports',
    category: 'Left navigation',
    title: 'Reporting',
    body: 'See how your hiring is performing — openings, applicants and engagement at a glance.',
    anchor: 'nav-reporting',
    arrow: 'right',
    leftNav: true,
    navFocus: 'nav-reporting',
  },
  {
    id: 'company',
    // CNCT-6779: the hub, which is where the Company row now leads. Pointing this at
    // /customer would land on /settings via that path's redirect, and runStep reads a
    // post-navigation URL that does not match the target as "gated" and skips the step.
    route: '/settings',
    category: 'Left navigation',
    title: 'Company',
    // Deliberately does NOT promise a company profile: that area is behind a flag that is off and its
    // destination is an unimplemented placeholder. The wording stays correct if the flag is ever turned
    // on — it describes the area rather than enumerating its children.
    body: 'Set up your company here: departments and locations, marketing channels, and your careers page.',
    anchor: 'nav-company',
    arrow: 'right',
    leftNav: true,
    navFocus: 'nav-company',
  },
  {
    id: 'favorites',
    // No route: Favorites is a section of the menu, not a destination — it changes no screen, so the
    // walk stays on the Company screen the previous step landed on.
    category: 'Left navigation',
    title: 'Favorites',
    body: 'Pinned shortcuts live at the top of the menu. Star any row in the navigation to add it here, and click the star again to remove it.',
    anchor: 'nav-favorites',
    arrow: 'right',
    leftNav: true,
    // No navFocus, deliberately: every nav group collapses, and the Favorites section owns its own
    // expand state, so it is left expanded and alone at the top of the rail. A user who has starred
    // nothing has no Favorites section at all, so the step finds no anchor and is skipped.
  },
  {
    id: 'account',
    route: '/account',
    category: 'Account menu',
    title: 'My Account',
    body: 'Your personal profile and preferences — name, notifications and security. Open it from the account menu, top right.',
    anchor: 'account-my-account',
    arrow: 'left',
    accountMenu: true,
  },
  {
    id: 'done',
    title: "That's the tour",
    // CNCT-6779: the header compass is desktop-only now, so below `lg` this points at the
    // account menu's own "Take a tour" — the same launcher, in the chrome that still has it.
    // The copy names both, since one card serves both widths.
    body: "You've seen the main areas — Openings, Pipelines, Messages, Campaigns, Reporting and Company in the left menu, plus My Account in the account menu. Start this tour again anytime: the compass in the header, or Take a tour in the account menu.",
    anchor: 'header-tour-launcher',
    arrow: 'bottom',
    narrow: { anchor: 'account-take-a-tour', arrow: 'left', accountMenu: true },
  },
]

/** Viewport width assumed when there is no window to measure (SSR/tests): treat the rail as visible. */
const LEFT_NAV_ASSUMED_WIDTH = 1440

/**
 * Driver for the guided walkthrough. It holds the ordered step list and, per step, navigates to the
 * area's real nav destination, publishes what the step needs from the app chrome (which nav group to
 * hold expanded, whether the account menu is open) via `TourFocusService`, confirms the step's subject
 * is actually on screen, and exposes the step for `TourCardComponent` to narrate and
 * `TourIndicatorComponent` to ring.
 *
 * The card itself never moves (design-contract.md → §Surface 3.2): narration is fixed bottom-right and
 * only the indicators travel, so this service positions nothing — it names the subject and lets the
 * indicator layer measure it.
 *
 * Per navigating step: `router.navigateByUrl(route)` then, if `TourEngineService.urlMatchesTarget(route)`
 * is false (a guard redirect / gated / flag-off area), the step is skipped in the direction of travel.
 * A step whose `anchor` never appears — a permission-hidden menu entry, a disabled feature — is skipped
 * the same way rather than showing an empty highlight. The intro carries no route and no anchor, so a
 * forward walk always has at least one resolvable stop and can never end empty.
 *
 * A monotonic run token makes it interruptible and idempotent: every `start()`/`stop()` bumps it and every
 * `await` re-checks it, so a `stop()` or restart mid-navigation aborts before emitting a stale step.
 */
@Injectable({ providedIn: 'root' })
export class GuideService {
  /** The ordered walk (module-level constant; instance ref keeps it easy to read/override in tests). */
  readonly steps: IGuideStep[] = GUIDE_STEPS

  private index = -1
  /** Whether the walk actually navigated the user off their starting screen (past the no-route intro). Drives
   *  the "return to the default route when the tour ends" behavior — a tour closed on the intro leaves the
   *  user where they were. */
  private navigated = false
  /** Guards a rapid double-advance while one navigation is still resolving. */
  private resolving = false
  /** Monotonic run token: every `start()`/`stop()` bumps it so an in-flight walk aborts once superseded. */
  private runToken = 0

  private readonly activeSubject = new BehaviorSubject<boolean>(false)
  /** Whether the walkthrough is currently running (drives the card's visibility). */
  readonly active$ = this.activeSubject.asObservable()

  private readonly currentStepSubject = new BehaviorSubject<IGuideActiveStep | null>(null)
  /** The current stop the card renders (or `null` when no walk is running). */
  readonly currentStep$ = this.currentStepSubject.asObservable()

  private readonly finishedSubject = new Subject<void>()
  /** Emits once when the walk ends (completed, skipped, or all-unresolvable). */
  readonly finished$ = this.finishedSubject.asObservable()

  constructor(
    private router: Router,
    private tourEngine: TourEngineService,
    private navigation: EmployerNavigationService,
    private tourFocus: TourFocusService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Reset to step 0 and run forward. Does NOT open the assistant pane (the card is its own surface); it
   * only closes the messaging panel, which the tour itself drives — a route-derived rail is left be.
   * Restarts
   * cleanly if already running (the run token supersedes any in-flight walk).
   */
  async start(): Promise<void> {
    const token = ++this.runToken
    this.clearTourPanel()
    this.tourFocus.clear()
    this.index = -1
    this.navigated = false
    this.currentStepSubject.next(null)
    this.activeSubject.next(true)
    this.resolving = true
    try {
      await this.runStep(0, 1, token)
    } finally {
      this.resolving = false
    }
  }

  /** Advance to the next resolvable stop. No-op when no walk is running or a resolve is already in flight. */
  next(): Promise<void> {
    if (!this.activeSubject.value || this.resolving) return Promise.resolve()
    this.resolving = true
    const token = this.runToken
    return this.runStep(this.index + 1, 1, token).finally(() => (this.resolving = false))
  }

  /** Return to the previous resolvable stop. No-op when no walk is running or a resolve is already in flight. */
  back(): Promise<void> {
    if (!this.activeSubject.value || this.resolving) return Promise.resolve()
    this.resolving = true
    const token = this.runToken
    return this.runStep(this.index - 1, -1, token).finally(() => (this.resolving = false))
  }

  /** End the walk, clear the current step, hand the nav/account chrome back to the user, and emit
   *  `finished$`. Idempotent. When the walk actually moved the user through screens, leave them back on
   *  the app's default route (not on the last stop's screen). */
  stop(): void {
    this.runToken++
    this.index = -1
    this.tourFocus.clear()
    this.clearTourPanel()
    if (this.activeSubject.value) {
      const returnToDefault = this.navigated
      this.navigated = false
      this.currentStepSubject.next(null)
      this.activeSubject.next(false)
      this.finishedSubject.next()
      if (returnToDefault) void this.router.navigateByUrl(ROUTES.JOBS)
    }
  }

  /**
   * Resolve the stop at `index`, skipping in `dir` when its navigation redirects (guard block / gated /
   * flag-off) or its subject is not on screen for this user. Forward overflow ends the walk; backward
   * underflow is a no-op (Back on the first step stays put). Recursion is bounded by `steps.length`, and
   * every await re-checks `token` so a `stop()`/restart mid-navigation aborts before emitting a stale step.
   */
  private async runStep(index: number, dir: 1 | -1, token: number): Promise<void> {
    if (token !== this.runToken) return
    if (index < 0) return
    if (index >= this.steps.length) {
      this.stop()
      return
    }
    const step = this.steps[index]

    if (step.route) {
      try {
        await this.router.navigateByUrl(step.route)
      } catch {
        // A thrown navigation is inaccessible; the URL check below skips it.
      }
      if (token !== this.runToken) return
      if (!this.tourEngine.urlMatchesTarget(step.route)) {
        await this.runStep(index + dir, dir, token)
        return
      }
      // A navigating stop resolved — the walk has moved the user off their starting screen, so on end
      // we return them to the default route rather than stranding them on the last stop.
      this.navigated = true
    }

    // Publish before resolving the anchor: the chrome the subject lives in (its nav group, the account
    // dropdown) has to open before the element is there to find. Always published, on every step, so a
    // previous step's expanded group or open dropdown never lingers into this one.
    this.tourFocus.setStepFocus({ nav: step.navFocus ?? null, accountMenu: needsAccountMenu(step, this.viewportWidth()) })
    this.applyMessagingPanel(step)

    if (!(await this.subjectOnScreen(step))) {
      if (token !== this.runToken) return
      await this.runStep(index + dir, dir, token)
      return
    }
    if (token !== this.runToken) return

    this.index = index
    this.currentStepSubject.next({ step, index, total: this.steps.length })
  }

  /**
   * Open the extended messaging panel for the step that wants it, and make sure no other step leaves it
   * open. Called after the step's navigation has settled, because a navigation clears the panel on its
   * way past (`MainLayoutComponent.resetStaleExtendedNav` — messaging owns no URL of its own).
   *
   * Desktop only: the panel column does not exist below `lg`, where the row expands its conversation
   * groups inline instead.
   */
  private applyMessagingPanel(step: IGuideStep): void {
    if (step.messagingPanel && isLeftNavVisible(this.viewportWidth())) {
      this.navigation.setExtendedNavContent(ExtendedNavId.Messaging)
      return
    }
    this.clearTourPanel()
  }

  /**
   * Clears only the panel the tour itself opens.
   *
   * This used to clear whatever panel was showing, so a walk tore down the Settings and
   * Reports rails too. Those are not stray chrome — they are route-derived context that
   * the shell opens for the URL you are on, so the tour was fighting its own navigation:
   * the Company step lands on the hub, the shell opens the Settings rail for it, and the
   * next line closed it again. On a narrow viewport that also took the Settings dropdown
   * trigger with it, since the trigger only renders while that rail is open.
   *
   * Messaging is the one the tour drives (`messagingPanel` steps), so it is the one the
   * tour is entitled to put away.
   */
  private clearTourPanel(): void {
    if (this.navigation.extendedNavContent$.value === ExtendedNavId.Messaging) {
      this.navigation.resetExtendedNavContent()
    }
  }

  /**
   * Whether this step's subject exists for this user. A step with no anchor (the intro) always resolves.
   * Otherwise the effective anchor for the current viewport is polled for briefly — a permission-hidden
   * menu entry or a disabled feature simply never appears, and the step is skipped rather than showing an
   * empty highlight.
   */
  private async subjectOnScreen(step: IGuideStep): Promise<boolean> {
    const target = resolveTourTarget(step, this.viewportWidth())
    if (!target.anchor) return true
    return !!(await waitForTourAnchor(this.document, target.anchor))
  }

  private viewportWidth(): number {
    return this.document.defaultView?.innerWidth ?? LEFT_NAV_ASSUMED_WIDTH
  }
}
